import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const NewsSection = () => {
  const theme = useTheme();

  const news = [
    {
      id: 1,
      title: '2025年马来西亚大学最新申请政策解读',
      excerpt: '详细解析马来西亚各大学2025年的最新申请要求、截止日期和奖学金政策变化...',
      category: '政策解读',
      author: '留学顾问团队',
      date: '2024-12-15',
      readTime: '5分钟阅读',
      image: '/images/news/policy-2025.jpg',
      trending: true,
    },
    {
      id: 2,
      title: '马来西亚生活成本全面分析：2025版',
      excerpt: '从住宿、饮食到交通，全面分析马来西亚主要城市的生活成本，帮助您做好留学预算...',
      category: '生活指南',
      author: '海外生活顾问',
      date: '2024-12-10',
      readTime: '8分钟阅读',
      image: '/images/news/living-cost.jpg',
      trending: false,
    },
    {
      id: 3,
      title: '马来亚大学工程学院最新排名及专业介绍',
      excerpt: '马来亚大学工程学院在最新QS排名中的表现，以及各专业的课程设置和就业前景...',
      category: '院校资讯',
      author: '院校研究员',
      date: '2024-12-08',
      readTime: '6分钟阅读',
      image: '/images/news/um-engineering.jpg',
      trending: false,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case '政策解读':
        return theme.palette.error.main;
      case '生活指南':
        return theme.palette.success.main;
      case '院校资讯':
        return theme.palette.primary.main;
      default:
        return theme.palette.secondary.main;
    }
  };

  return (
    <Box sx={{ py: 8, backgroundColor: theme.palette.background.default }}>
      <Container maxWidth="xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: theme.palette.text.primary,
              }}
            >
              留学资讯
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 600,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.2rem' },
              }}
            >
              获取最新的留学政策、院校动态和生活指南
            </Typography>
          </Box>

          {/* News Grid */}
          <Grid container spacing={4}>
            {news.map((article, index) => (
              <Grid item xs={12} md={4} key={article.id}>
                <motion.div variants={cardVariants}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                    }}
                  >
                    {/* Image Placeholder */}
                    <Box
                      sx={{
                        height: 200,
                        background: `linear-gradient(135deg, ${alpha(getCategoryColor(article.category), 0.8)}, ${alpha(getCategoryColor(article.category), 0.6)})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          opacity: 0.3,
                        }}
                      >
                        NEWS
                      </Typography>
                      
                      {/* Trending Badge */}
                      {article.trending && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 16,
                            left: 16,
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: alpha(theme.palette.warning.main, 0.9),
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 20,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}
                        >
                          <TrendingIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          热门
                        </Box>
                      )}

                      {/* Category Badge */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          color: getCategoryColor(article.category),
                          px: 2,
                          py: 0.5,
                          borderRadius: 20,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {article.category}
                      </Box>
                    </Box>

                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      {/* Article Title */}
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 2,
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          lineHeight: 1.4,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {article.title}
                      </Typography>

                      {/* Article Excerpt */}
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 3,
                          color: theme.palette.text.secondary,
                          lineHeight: 1.6,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {article.excerpt}
                      </Typography>

                      {/* Article Meta */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <PersonIcon sx={{ fontSize: 16, mr: 0.5, color: theme.palette.text.secondary }} />
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {article.author}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarIcon sx={{ fontSize: 16, mr: 0.5, color: theme.palette.text.secondary }} />
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                              {article.date}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {article.readTime}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Read More Button */}
                      <Button
                        variant="outlined"
                        fullWidth
                        sx={{
                          borderColor: getCategoryColor(article.category),
                          color: getCategoryColor(article.category),
                          fontWeight: 600,
                          py: 1,
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: alpha(getCategoryColor(article.category), 0.1),
                            borderColor: getCategoryColor(article.category),
                          },
                        }}
                      >
                        阅读全文
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* View All Button */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 25,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              查看更多资讯
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default NewsSection;
